import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUp, ArrowDown, ArrowUpRight, ArrowDownRight,
  Loader2, Lock, Clock, Trophy, XCircle, Activity,
} from "lucide-react";
import { LiveChart } from "@/components/LiveChart";
import { usePrices, fmtPrice } from "@/hooks/usePrices";
import { useDailyTrade, resetLabel, resultLabel } from "@/hooks/useDailyTrade";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GameEntryDialog } from "@/components/GameRules";

const TIMEFRAMES = [
  { key: "30 quotes", samples: 30 },
  { key: "60 quotes", samples: 60 },
  { key: "120 quotes", samples: 120 },
];

export default function CoinDetail() {
  const { symbol } = useParams();
  const sym = (symbol || "").toUpperCase();
  const navigate = useNavigate();
  const { coins } = usePrices(15000);
  const { refresh } = useAuth();
  const { trade, unlocked, nextReset, placing, loading, place, schedule } = useDailyTrade(refresh, {
    resultToast: false,
  });
  const [tf, setTf] = useState("60 quotes");
  const [entry, setEntry] = useState(null);

  const coin = coins.find((c) => c.symbol === sym);
  const up = (coin?.change24h || 0) >= 0;
  const samples = TIMEFRAMES.find((t) => t.key === tf)?.samples || 60;
  const displayPrice = coin?.price;
  const isMine = trade && trade.symbol === sym;
  const locked = !!trade;

  const handlePlace = (dir) => {
    if (!unlocked) {
      navigate("/wallet");
      return;
    }
    setEntry({ symbol: sym, direction: dir });
  };

  const handleConfirm = async () => {
    if (await place(sym, entry.direction)) setEntry(null);
  };

  return (
    <div className="coin-detail-page grain min-h-screen">
      {/* top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0B0E14]/80 border-b border-[#1E293B]">
        <div className="max-w-md mx-auto px-4 h-14 flex items-center gap-3">
          <button
            data-testid="coin-back-btn"
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-[#121721] border border-[#1E293B] flex items-center justify-center"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2">
            {coin?.image && <img src={coin.image} alt="" className="w-6 h-6 rounded-full" />}
            <span className="font-display font-bold text-lg">{sym}/USDT</span>
          </div>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-[#00E676] font-semibold">
            {coin?.live ? "LIVE" : "REFERENCE"}
          </span>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 pt-4">
        {!coin ? (
          <div className="flex justify-center pt-24">
            <Loader2 className="animate-spin text-[#00E676]" />
          </div>
        ) : (
          <>
            {/* price header */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-xs text-slate-400">{coin.name}</div>
              <div className="flex items-end gap-3">
                <div
                  data-testid="coin-live-price"
                  className={`font-mono-num text-4xl font-bold ${up ? "text-[#F8FAFC]" : "text-[#F8FAFC]"}`}
                >
                  ${fmtPrice(displayPrice)}
                </div>
                <div
                  className={`font-mono-num text-sm font-semibold flex items-center gap-0.5 mb-1.5 ${
                    up ? "text-[#00E676]" : "text-[#FF3B30]"
                  }`}
                >
                  {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {Math.abs(coin.change24h || 0).toFixed(2)}%
                </div>
              </div>
            </motion.div>

            {/* timeframe */}
            <div className="flex gap-1.5 mt-4">
              {TIMEFRAMES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`tf-${t.key}`}
                  onClick={() => setTf(t.key)}
                  className={`px-3 h-8 rounded-lg text-xs font-mono-num font-semibold border transition-colors ${
                    tf === t.key
                      ? "border-[#00E676] bg-[#0E2E25] text-[#00E676]"
                      : "border-[#1E293B] bg-[#121721] text-slate-400"
                  }`}
                >
                  {t.key}
                </button>
              ))}
            </div>

            {/* live chart */}
            <div className="mt-3 rounded-2xl border border-[#1E293B] bg-[#121721] p-2 pt-3">
              <LiveChart
                key={sym + tf}
                seed={coin.sparkline}
                up={up}
                samples={samples}
                height={260}
              />
            </div>

            {/* stats */}
            <div className="grid grid-cols-3 gap-2 mt-3">
              {[
                { label: "24h High", value: `$${fmtPrice(coin.high24h)}`, cls: "text-[#00E676]" },
                { label: "24h Low", value: `$${fmtPrice(coin.low24h)}`, cls: "text-[#FF3B30]" },
                { label: "Change", value: `${up ? "+" : ""}${(coin.change24h || 0).toFixed(2)}%`, cls: up ? "text-[#00E676]" : "text-[#FF3B30]" },
              ].map((s) => (
                <div key={s.label} className="rounded-xl border border-[#1E293B] bg-[#121721] p-3">
                  <div className="text-[10px] text-slate-500">{s.label}</div>
                  <div className={`font-mono-num text-sm font-semibold ${s.cls}`}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* trade status is shown in the sticky bottom bar */}

            {!unlocked && !loading && (
              <div className="mt-4 rounded-xl border border-[#F59E0B]/30 bg-[#1E2330] px-3 py-2.5 flex items-center gap-2 text-xs text-slate-300">
                <Lock size={14} className="text-[#F59E0B]" />
                Deposit 50 USDT to unlock withdrawals. Market charts remain available for reference.
              </div>
            )}

            {sym === "USDT" && (
              <div className="mt-4 rounded-xl border border-[#1E293B] bg-[#121721] px-3 py-2.5 flex items-center gap-2 text-xs text-slate-400">
                <Activity size={14} /> USDT is a stablecoin — trading is available on other assets.
              </div>
            )}
          </>
        )}
      </div>

      {/* sticky bottom action / status */}
      {coin && sym !== "USDT" && (
        <div data-testid="coin-detail-actions" className="coin-detail-actions fixed left-1/2 -translate-x-1/2 w-full max-w-md z-40 px-4 pb-3 pt-6 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14] to-transparent">
          {trade && trade.status === "pending" ? (
            <div className="rounded-xl border border-[#1E293B] bg-[#121721] px-3 py-3 flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${
                  trade.direction === "pull" ? "bg-[#0E2E25] text-[#00E676]" : "bg-[#3A141A] text-[#FF3B30]"
                }`}
              >
                {trade.direction === "pull" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                {trade.direction === "pull" ? "PULL" : "CALL"} · {trade.symbol}
              </span>
              <span className="text-xs text-slate-400">Results at {resultLabel(trade.resolve_at)}</span>
              <Loader2 className="animate-spin text-slate-500 ml-auto" size={14} />
            </div>
          ) : trade ? (
            <div
              data-testid="detail-result-banner"
              className={`rounded-xl border px-3 py-3 flex items-center gap-3 ${
                trade.status === "won" ? "border-[#00E676]/40 bg-[#0E2E25]" : "border-[#FF3B30]/40 bg-[#3A141A]"
              }`}
            >
              {trade.status === "won" ? (
                <Trophy className="text-[#00E676]" size={22} />
              ) : (
                <XCircle className="text-[#FF3B30]" size={22} />
              )}
              <div>
                <div className="text-sm font-semibold">
                  {trade.status === "won" ? "You won today" : "You lost today"}{" "}
                  <span className={`font-mono-num ${trade.status === "won" ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                    {trade.payout > 0 ? "+" : ""}
                    {trade.payout} USDT
                  </span>
                </div>
                <div className="text-[11px] text-slate-400 flex items-center gap-1">
                  <Clock size={11} /> Next bid opens {resetLabel(nextReset)}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid={`detail-pull-${sym.toLowerCase()}`}
                disabled={placing !== ""}
                onClick={() => handlePlace("pull")}
                className="h-14 font-display font-extrabold text-base rounded-xl gap-1.5 bg-[#00E676] hover:bg-[#00C853] text-black disabled:opacity-40"
              >
                {placing === sym + "pull" ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>
                    <ArrowUp size={18} strokeWidth={3} /> PULL
                  </>
                )}
              </Button>
              <Button
                data-testid={`detail-call-${sym.toLowerCase()}`}
                disabled={placing !== ""}
                onClick={() => handlePlace("call")}
                className="h-14 font-display font-extrabold text-base rounded-xl gap-1.5 bg-[#FF3B30] hover:bg-[#D50000] text-white disabled:opacity-40"
              >
                {placing === sym + "call" ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>
                    <ArrowDown size={18} strokeWidth={3} /> CALL
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      <GameEntryDialog
        entry={entry}
        onClose={() => setEntry(null)}
        onConfirm={handleConfirm}
        busy={placing !== ""}
        resultAt={schedule?.next_result_at}
      />
    </div>
  );
}
